interface Certificate {
    name: string;
    description: string;
    logo: string;
}

export const getEnvironmentalCertificate = (key: string): Certificate[] => {
    const certificateTypes = certificateMappings[key] || newCertificateMapping[key] || [];
    return certificateTypes.sort((type1, type2) => type1 - type2).map((type) => certificates[type]);
};

enum CertificateType {
    ECO_LIGHTHOUSE = 0,
    SWAN_LABEL = 1,
    GREEN_KEY = 2,
    ISO_14001 = 3,
    ECOTOURISM_NORWAY = 4,
}

const certificates: Record<CertificateType, Certificate> = {
    [CertificateType.ECO_LIGHTHOUSE]: {
        name: 'Miljøfyrtårn',
        description:
            'Miljøfyrtårn-bedrifter jobber mot å tilfredsstille krav og implementere miljøtiltak på en systematisk og kontinuerlig måte ' +
            'for å skape en mer miljøvennlig drift og et tryggere arbeidsmiljø. Sertifiserte bedrifter er underlagt en uavhengig ' +
            'vurdering og må gjennomgå en sertifiseringsprosess hvert tredje år.',
        logo: require('../../../../../../../public/images/certificates/eco-lighthouse.png').default?.src,
    },
    [CertificateType.SWAN_LABEL]: {
        name: 'Svanemerket',
        description:
            'Mer enn 5000 produkter og tjenester i Norge er sertifisert med Svanemerket, som indikerer at de tilfredsstiller strenge ' +
            'krav innen energieffektivitet, bruk av miljøvennlige materialer og kjemikalier. Helt fra råvarer til sluttprodukt og ' +
            'avfallshåndtering.',
        logo: require('../../../../../../../public/images/certificates/swan-label.png').default?.src,
    },
    [CertificateType.GREEN_KEY]: {
        name: 'Green Key',
        description:
            'Green Key er et globalt miljømerke for hoteller, overnattingssteder, campingplasser og attraksjoner. Sertifiserte ' +
            'virksomheter må oppfylle strenge kriterier for avfallshåndtering, energi-, vannforbruk, anskaffelser, grøntområder, ' +
            'samfunnsansvar og involvering av ansatte. Høye standarder opprettholdes gjennom en årlig sertifikatfornyelse, ' +
            'streng søknadsprosess, dokumentasjonskrav og hyppige revisjoner.',
        logo: require('../../../../../../../public/images/certificates/green-key.png').default?.src,
    },
    [CertificateType.ISO_14001]: {
        name: 'ISO-14001',
        description:
            'ISO 14001 sertifiseringen gis til bedrifter som har systemer av høy kvalitet og bidrar til gode effekter for miljøet.',
        logo: require('../../../../../../../public/images/certificates/iso-14001.png').default?.src,
    },
    [CertificateType.ECOTOURISM_NORWAY]: {
        name: 'Ecotourism Norway',
        description:
            'Denne nasjonale sertifiseringen tildeles bedrifter og operatører som holder et høyt internasjonalt nivå innen økoturisme. ' +
            'Mer enn 100 strenge kriterier innen miljøprestasjoner, vertskap, integrering av lokalsamfunn og innkjøp må oppfylles og ' +
            'ofte forbedres. Sertifikatet fornyes hvert tredje år.',
        logo: require('../../../../../../../public/images/certificates/ecotourism-norway.png').default?.src,
    },
};

const certificateMappings: Record<string, CertificateType[]> = {
    Thon_Hotel_Polar: [CertificateType.ECO_LIGHTHOUSE],
    Comfort_Hotel_Xpress_Tromso: [CertificateType.ISO_14001],
    Fru_Haugans_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Sure_Hotel_by_Best_Western_Hotel_Ole_Tobias: [CertificateType.ISO_14001],
    Henningsvaer_Bryggehotel_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Skagen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Collection_Hotel_Grand_Bodo: [CertificateType.ISO_14001],
    Rognan_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Nordlys: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Havet: [CertificateType.SWAN_LABEL],
    Scandic_Kirkenes: [CertificateType.SWAN_LABEL],
    Scandic_Bryggen: [CertificateType.SWAN_LABEL],
    Scandic_Karasjok: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Kirkenes: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Andrikken: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Alta: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Grand_Royal: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Aurora: [CertificateType.ISO_14001],
    Scandic_Svolvaer: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Alta: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Collection_Hotel_With: [CertificateType.ISO_14001],
    Radisson_Blu_Hotel_Tromso: [CertificateType.GREEN_KEY],
    Scandic_Meyergarden: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Bronnoysund: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Bodo: [CertificateType.SWAN_LABEL],
    Clarion_Hotel_The_Edge: [CertificateType.ISO_14001],
    Quality_Hotel_Saga: [CertificateType.ISO_14001],
    Scandic_Grand_Tromso: [CertificateType.SWAN_LABEL],
    Scandic_Ishavshotel: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Tromso: [CertificateType.ECO_LIGHTHOUSE],
    Valldal_Fjordhotell_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Otta: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Mountain_Resort_Residences_Trysil: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Leto_Arena: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Bergen_City: [CertificateType.SWAN_LABEL],
    Grand_Hotel_Terminus: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Bergen_Airport: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Hotel_Admiral: [CertificateType.ISO_14001],
    Thon_Hotel_Bristol_Oslo: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Orion: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Park_Bergen: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Rosenkrantz: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Kokstad: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Edvard_Grieg: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Havnekontoret: [CertificateType.ISO_14001],
    First_Hotel_Atlantica: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Norge_by_Scandic: [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL],
    Quality_Hotel_Waterfront_Alesund: [CertificateType.ISO_14001],
    Thon_Hotel_Fosnavag: [CertificateType.ECO_LIGHTHOUSE],
    Thorbjornrud_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Saga: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Sandnes: [CertificateType.ECO_LIGHTHOUSE],
    Hotell_Jaeren: [CertificateType.ECO_LIGHTHOUSE],
    Comfort_Hotel_Stavanger: [CertificateType.ISO_14001],
    Best_Western_Havly_Hotell: [CertificateType.SWAN_LABEL, CertificateType.ISO_14001],
    Scandic_Stavanger_Park: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Stavanger_Airport: [CertificateType.ISO_14001],
    Scandic_Stavanger_Airport: [CertificateType.SWAN_LABEL],
    Scandic_Stavanger_Forus: [CertificateType.SWAN_LABEL],
    Scandic_Stavanger_City: [CertificateType.SWAN_LABEL],
    Comfort_Hotel_Floro: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Hammer: [CertificateType.ISO_14001],
    Thon_Hotel_Skeikampen: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Laegreid_Hotell: [CertificateType.ECO_LIGHTHOUSE],
    Jostedal_Camping: [CertificateType.GREEN_KEY],
    Scandic_Neptun: [CertificateType.SWAN_LABEL],
    Panorama_Hotell_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Ornen: [CertificateType.SWAN_LABEL],
    Grand_Hotel_Egersund: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Stavanger: [CertificateType.ECO_LIGHTHOUSE],
    Fjaerland_Fjordstue_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    '292_Aurland': [CertificateType.ECO_LIGHTHOUSE],
    First_Hotel_Victoria: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Hamar: [CertificateType.SWAN_LABEL],
    Lillesand_Hotel_Norge: [CertificateType.ECO_LIGHTHOUSE],
    Erzscheidergaarden: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Halden: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Harstad: [CertificateType.ECO_LIGHTHOUSE],
    Lampeland_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Grimstad: [CertificateType.SWAN_LABEL],
    Clarion_Collection_Hotel_Arcticus: [CertificateType.ISO_14001],
    Thon_Hotel_Spectrum: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Terminus: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Plaza_Hotel_Oslo: [CertificateType.GREEN_KEY],
    Thon_Hotel_Opera: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Tonsberg_Brygge: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Cecil: [CertificateType.ECO_LIGHTHOUSE],
    Grand_Hotel_Asgardstrand: [CertificateType.ECO_LIGHTHOUSE],
    Losby_Gods_Manor: [CertificateType.ECO_LIGHTHOUSE],
    Zander_K_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Bo_Hotel_Bo: [CertificateType.ECO_LIGHTHOUSE],
    Skarsnuten_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Pond: [CertificateType.ISO_14001],
    Thon_Hotel_Munch: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Parken: [CertificateType.ECO_LIGHTHOUSE],
    Cochs_Pensjonat: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Byporten: [CertificateType.SWAN_LABEL],
    Scandic_Solli: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Vika_Atrium: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Slottsparken: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Gyldenlove: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Astoria: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Oslo_City: [CertificateType.SWAN_LABEL],
    First_Hotel_Millennium: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Holberg: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Arena: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Linne: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Holmenkollen_Park: [CertificateType.SWAN_LABEL],
    Radisson_Blu_Hotel_Nydalen_Oslo: [CertificateType.GREEN_KEY],
    Scandic_Victoria: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Oslo_Panorama: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Ullevaal_Stadion: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Expo: [CertificateType.ISO_14001],
    Scandic_Fornebu: [CertificateType.SWAN_LABEL],
    Jegtvolden_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Son_Spa: [CertificateType.ISO_14001],
    Ustedalen_Hotel_Hol: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Moldefjord: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Arendal: [CertificateType.ECO_LIGHTHOUSE],
    Bergen_Bors_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kongsvinger_Castle_Hotel_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Flesland_Airport: [CertificateType.SWAN_LABEL],
    Svalbard_Hotell_The_Vault: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Grand_Steinkjer: [CertificateType.ISO_14001],
    Hotel_Refsnes_Gods: [CertificateType.ECO_LIGHTHOUSE],
    Augustin_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Skaergaarden: [CertificateType.ISO_14001],
    Hotel_Vic: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Hoyers: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Parken: [CertificateType.SWAN_LABEL],
    Hotel_Noreg: [CertificateType.SWAN_LABEL],
    Hotel_Union_Oye: [CertificateType.ECO_LIGHTHOUSE],
    Runde_Miljosenter: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Strand: [CertificateType.ISO_14001],
    Honne_Hotell_Konferansesenter: [CertificateType.GREEN_KEY],
    Klaekken_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Maritim: [CertificateType.SWAN_LABEL],
    GamlaVaerket_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Residence: [CertificateType.ISO_14001],
    Hotel_Sverre: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Atlantic_Hotel_Stavanger: [CertificateType.GREEN_KEY],
    Victoria_Hotel_Stavanger: [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL],
    Scandic_Forum: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Maritim: [CertificateType.ECO_LIGHTHOUSE],
    St_Svithun_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Energihotellet: [CertificateType.ECO_LIGHTHOUSE],
    Bekkjarvik_Gjestgiveri: [CertificateType.ECO_LIGHTHOUSE],
    Bomlo_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Birkebeineren_Hotel_Apartments: [CertificateType.ECO_LIGHTHOUSE],
    Lillehammer_Stasjonen_Hotel_Hostel: [CertificateType.GREEN_KEY],
    TOP_Molla_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Lillehammer_Hotel: [CertificateType.SWAN_LABEL],
    Scandic_Hafjell: [CertificateType.SWAN_LABEL],
    Hafjell_Resort_Jaertunet: [CertificateType.ECO_LIGHTHOUSE],
    Storefjell_Resort_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Valdres: [CertificateType.SWAN_LABEL],
    Dalen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kviknes_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Sogndal: [CertificateType.ISO_14001],
    Bergstadens_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Flamsbrygga_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Lindstroem_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Jolster: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sunnfjord_Hotel_Spa: [CertificateType.SWAN_LABEL],
    Scandic_Royal_Stavanger: [CertificateType.GREEN_KEY],
    Grand_Hotel_Oslo_by_Scandic: [CertificateType.SWAN_LABEL],
    TrollAktiv_Adventure_Park_Evje: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Park_Sandefjord: [CertificateType.SWAN_LABEL],
    Roros_Hotell_Bad_Velvaere: [CertificateType.ECO_LIGHTHOUSE],
    Basecamp_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Vertshuset_Roros: [CertificateType.ECO_LIGHTHOUSE],
    Bardola_Hoyfjellshotell: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Kristiansund: [CertificateType.ECO_LIGHTHOUSE],
    Lysebu_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Farsund_Resort: [CertificateType.SWAN_LABEL],
    Tregde_Ferie: [CertificateType.ECO_LIGHTHOUSE],
    Knutholmen: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sorlandet: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Norge: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Kristiansand_Bystranda: [CertificateType.SWAN_LABEL],
    Hallingdal_Feriepark: [CertificateType.ECO_LIGHTHOUSE],
    Hustadvika_Guesthouse_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Skaslien_Hotel_Guesthouse: [CertificateType.ECO_LIGHTHOUSE],
    Dolen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Tingvold_Park_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Continental_Oslo: [CertificateType.ECO_LIGHTHOUSE],
    Solvgarden_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Backlund: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Vulkan: [CertificateType.SWAN_LABEL],
    Sundvolden_Hotel: [CertificateType.SWAN_LABEL],
    Radisson_Blu_Scandinavia_Hotel_Oslo: [CertificateType.GREEN_KEY],
    Scandic_St_Olavs_Plass: [CertificateType.SWAN_LABEL],
    Molde_Fjordstuer_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Haholmen_Havstuer_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Alexandra_Molde: [CertificateType.ISO_14001],
    Scandic_Seilet: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Kristiansand: [CertificateType.ECO_LIGHTHOUSE],
    Langedrag_Naturpark: [CertificateType.ECO_LIGHTHOUSE],
    Engo_Gard_Hotel_Restaurant: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Rock_City: [CertificateType.SWAN_LABEL],
    Vauldalen: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Klubben: [CertificateType.ISO_14001],
    Holmen_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Leangkollen: [CertificateType.ISO_14001],
    Scandic_Asker: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Entry: [CertificateType.ISO_14001],
    Scandic_Gardermoen: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Fredrikstad: [CertificateType.ISO_14001],
    Quality_Hotel_Olavsgaard: [CertificateType.ISO_14001],
    Quality_Hotel_Sarpsborg: [CertificateType.ISO_14001],
    Quality_Hotel_Grand_Farris: [CertificateType.ISO_14001],
    Mandal_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kragero_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Strand_Hotel_Fevik_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Ulstein: [CertificateType.ISO_14001],
    Vestlia_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Revsnes_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Ona_Havstuer_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Finnoy_Havstuer_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Roisheim_Hotel_Skysstasjon: [CertificateType.ECO_LIGHTHOUSE],
    Smarthotel_Forus: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Storo: [CertificateType.ECO_LIGHTHOUSE],
    Holmvik_Brygge_Nyksund: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Alesund: [CertificateType.ISO_14001],
    Visnes_Hotel_Stryn: [CertificateType.ECO_LIGHTHOUSE],
    Innvik_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Sure_Hotel_by_Best_Western_Harstad_Narvik_Airport: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Bjorneparken: [CertificateType.ECO_LIGHTHOUSE],
    Solstrand_Hotel_Bad: [CertificateType.ECO_LIGHTHOUSE],
    Sola_Strand_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kronen_Gaard_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Stord_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Pellestova_Hotell_Hafjell: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Hotel_The_Hub: [CertificateType.ISO_14001],
    Hurdalsjoen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Caledonien_Hotel_Kristiansand: [CertificateType.GREEN_KEY],
    Thon_Hotel_Horten: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sjolyst: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Hallingdal: [CertificateType.ECO_LIGHTHOUSE],
    Kjobmandsgaarden_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Havna_Tjome: [CertificateType.SWAN_LABEL],
    Hotel_Union_Geiranger_Bad_Spa: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Geiranger: [CertificateType.ECO_LIGHTHOUSE],
    Rederiet_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Floro: [CertificateType.ISO_14001],
    Kirkvollen_pilegrimsgard: [CertificateType.ECOTOURISM_NORWAY],
};

const newCertificateMapping = {
    '2379619': [CertificateType.ECO_LIGHTHOUSE], //Vauldalen
    '2110991': [CertificateType.ECO_LIGHTHOUSE], //292_Aurland
    '494326': [CertificateType.ECO_LIGHTHOUSE], //Langedrag_Naturpark
    '80962': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Hallingdal
    '85074': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Andrikken
    '159326': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Arendal
    '46': [CertificateType.SWAN_LABEL], //Scandic_Kokstad
    '128642': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_Havnekontoret
    '21379': [CertificateType.ISO_14001], //Clarion_Hotel_Admiral
    '79919': [CertificateType.ISO_14001], //Quality_Hotel_Edvard_Grieg
    '21367': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Rosenkrantz
    '149428': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_Grand_Bodo
    '408992': [CertificateType.SWAN_LABEL], //Scandic_Bodo
    '25723': [CertificateType.ECO_LIGHTHOUSE], //Skagen_Hotel
    '145647': [CertificateType.ECO_LIGHTHOUSE], //Dolen_Hotel
    '1757': [CertificateType.SWAN_LABEL], //Scandic_Valdres
    '159302': [CertificateType.ECO_LIGHTHOUSE], //Bardola_Hoyfjellshotell
    '343646': [CertificateType.ISO_14001], //Quality_Hotel_Strand
    '74809': [CertificateType.ECO_LIGHTHOUSE], //Storefjell_Resort_Hotel
    '39626': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_Arcticus
    '7778': [CertificateType.SWAN_LABEL], //Thon_Hotel_Saga
    '21874': [CertificateType.SWAN_LABEL], //Scandic_Kirkenes
    '1070229923': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Norge
    '10180': [CertificateType.ECO_LIGHTHOUSE], //Birkebeineren_Hotel_Apartments
    '21800': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_Hammer
    '87104': [CertificateType.SWAN_LABEL], //Scandic_Hafjell
    '2505947': [CertificateType.SWAN_LABEL], //Scandic_Lillehammer_Hotel
    '74771': [CertificateType.SWAN_LABEL], //Scandic_Meyergarden
    '8262': [CertificateType.ISO_14001], //Scandic_Alexandra_Molde
    '100449': [CertificateType.SWAN_LABEL], //Scandic_Seilet
    '13745': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Spectrum
    '21493': [CertificateType.ECO_LIGHTHOUSE], //First_Hotel_Millennium
    '9640': [CertificateType.SWAN_LABEL], //Scandic_Victoria
    '33617': [CertificateType.SWAN_LABEL], //Scandic_Holmenkollen_Park
    '59251': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Continental_Oslo
    '59240': [CertificateType.SWAN_LABEL], //Scandic_Byporten
    '59235': [CertificateType.ISO_14001], //Quality_Hotel_Grand_Royal
    '7443': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Vic
    '140090': [CertificateType.ECO_LIGHTHOUSE], //Rognan_Hotel
    '319511': [CertificateType.ECO_LIGHTHOUSE], //Roros_Hotell_Bad_Velvaere
    '140099': [CertificateType.ECO_LIGHTHOUSE], //Smarthotel_Forus
    '176126': [CertificateType.ECO_LIGHTHOUSE], //GamlaVaerket_Hotel
    '30313': [CertificateType.ECO_LIGHTHOUSE], //Best_Western_Laegreid_Hotell
    '2682284': [CertificateType.GREEN_KEY], //Radisson_Blu_Atlantic_Hotel_Stavanger
    '191488': [CertificateType.GREEN_KEY], //Scandic_Royal_Stavanger
    '39580': [CertificateType.ECO_LIGHTHOUSE], //Stord_Hotel
    '5889': [CertificateType.SWAN_LABEL], //Scandic_Svolvaer
    '84640': [CertificateType.SWAN_LABEL], //Scandic_Havna_Tjome
    '4677': [CertificateType.ISO_14001], //Hotel_Klubben
    '59262': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_Aurora
    '74744': [CertificateType.SWAN_LABEL], //Scandic_Grand_Tromso
    '14890': [CertificateType.GREEN_KEY], //Radisson_Blu_Hotel_Tromso
    '21635': [CertificateType.SWAN_LABEL], //Scandic_Ishavshotel
    '161229': [CertificateType.ISO_14001], //Quality_Hotel_Ulstein
    '7109': [CertificateType.ISO_14001], //Quality_Hotel_Saga
    '22094': [CertificateType.SWAN_LABEL], //Scandic_Park_Sandefjord
    '37443': [CertificateType.GREEN_KEY], //Radisson_Blu_Plaza_Hotel_Oslo
    '101088': [CertificateType.GREEN_KEY], //Radisson_Blu_Hotel_Nydalen_Oslo
    '74761': [CertificateType.SWAN_LABEL], //Scandic_Solli
    '31172': [CertificateType.SWAN_LABEL], //Scandic_St_Olavs_Plass
    '80960': [CertificateType.SWAN_LABEL], //Scandic_Karasjok
    '140101': [CertificateType.SWAN_LABEL], //Hotel_Noreg
    '74752': [CertificateType.ECO_LIGHTHOUSE], //First_Hotel_Atlantica
    '5556': [CertificateType.ECO_LIGHTHOUSE], //Kviknes_Hotel
    '7749028': [CertificateType.ECO_LIGHTHOUSE], //Augustin_Hotel
    '12218': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Orion
    '74822': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Union_Geiranger_Bad_Spa
    '82799': [CertificateType.SWAN_LABEL], //Scandic_Grimstad
    '159325': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Harstad
    '4683': [CertificateType.SWAN_LABEL], //Scandic_Sorlandet
    '5698': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Moldefjord
    '59243': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Terminus
    '59237': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Slottsparken
    '59244': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Vika_Atrium
    '35533': [CertificateType.ISO_14001], //Clarion_Hotel_The_Hub
    '159330': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Gyldenlove
    '37187': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Munch
    '34837': [CertificateType.SWAN_LABEL], //Grand_Hotel_Oslo_by_Scandic
    '159328': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Sandnes
    '59255': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Hoyers
    '25470': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Arena
    '1756': [CertificateType.SWAN_LABEL], //Scandic_Stavanger_Park
    '13543': [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL], //Victoria_Hotel_Stavanger
    '74720': [CertificateType.ECO_LIGHTHOUSE], //Bergstadens_Hotel
    '163838': [CertificateType.ISO_14001], //Quality_Hotel_Fredrikstad
    '6087806': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Horten
    '456470': [CertificateType.ECO_LIGHTHOUSE], //Kragero_Resort
    '74769': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Linne
    '302772': [CertificateType.ECO_LIGHTHOUSE], //Solstrand_Hotel_Bad
    '371825': [CertificateType.ECO_LIGHTHOUSE], //Grand_Hotel_Asgardstrand
    '96026': [CertificateType.ISO_14001], //Quality_Hotel_Floro
    '2238111': [CertificateType.SWAN_LABEL], //Scandic_Gardermoen
    '11563': [CertificateType.SWAN_LABEL], //Scandic_Bryggen
    '3465': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Bronnoysund
    '3346': [CertificateType.ISO_14001], //Quality_Hotel_Skaergaarden
    '642776': [CertificateType.ECO_LIGHTHOUSE], //Innvik_Fjordhotell
    '74827': [CertificateType.ISO_14001], //Comfort_Hotel_Floro
    '74758': [CertificateType.ECO_LIGHTHOUSE], //Hotell_Jaeren
    '202785': [CertificateType.ISO_14001], //Son_Spa
    '4678': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Tonsberg_Brygge
    '605998': [CertificateType.ECO_LIGHTHOUSE], //Haholmen_Havstuer_by_Classic_Norway
    '224219': [CertificateType.ECO_LIGHTHOUSE], //Lysebu_Hotel
    '396581': [CertificateType.SWAN_LABEL], //Sundvolden_Hotel
    '1917': [CertificateType.ISO_14001], //Quality_Hotel_Olavsgaard
    '358011': [CertificateType.ECO_LIGHTHOUSE], //Skarsnuten_Hotel
    '384331': [CertificateType.ECO_LIGHTHOUSE], //Basecamp_Hotel
    '74826': [CertificateType.ECO_LIGHTHOUSE], //Vestlia_Resort
    '375705': [CertificateType.ECO_LIGHTHOUSE], //Bo_Hotel_Bo
    '346545': [CertificateType.SWAN_LABEL], //Scandic_Stavanger_Airport
    '359618': [CertificateType.ECO_LIGHTHOUSE], //Roisheim_Hotel_Skysstasjon
    '5996': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Skeikampen
    '346546': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Kirkenes
    '337737': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Alta
    '354552': [CertificateType.ECO_LIGHTHOUSE], //Klaekken_Hotel
    '127024': [CertificateType.ECO_LIGHTHOUSE], //Lillesand_Hotel_Norge
    '396378': [CertificateType.ECO_LIGHTHOUSE], //Energihotellet
    '645574': [CertificateType.ECO_LIGHTHOUSE], //Radisson_Blu_Mountain_Resort_Residences_Trysil
    '621156': [CertificateType.ECO_LIGHTHOUSE], //Jegtvolden_Fjordhotell
    '341514': [CertificateType.ECO_LIGHTHOUSE], //Visnes_Hotel_Stryn
    '643989': [CertificateType.ECO_LIGHTHOUSE], //Revsnes_Hotel
    '640228': [CertificateType.ECO_LIGHTHOUSE], //Finnoy_Havstuer_By_Classic_Norway_Hotels
    '637807': [CertificateType.ECO_LIGHTHOUSE], //Bomlo_Hotel
    '417051': [CertificateType.ECO_LIGHTHOUSE], //Panorama_Hotell_Resort
    '621155': [CertificateType.ECO_LIGHTHOUSE], //Hustadvika_Guesthouse_By_Classic_Norway_Hotels
    '422651': [CertificateType.ECO_LIGHTHOUSE], //Rederiet_Hotel
    '652834': [CertificateType.ECO_LIGHTHOUSE], //Erzscheidergaarden
    '422982': [CertificateType.ECO_LIGHTHOUSE], //Thorbjornrud_Hotel
    '423001': [CertificateType.ECO_LIGHTHOUSE], //Tregde_Ferie
    '432072': [CertificateType.ECO_LIGHTHOUSE], //Skaslien_Hotel_Guesthouse
    '662141': [CertificateType.ECO_LIGHTHOUSE], //TrollAktiv_Adventure_Park_Evje
    '695470': [CertificateType.ECO_LIGHTHOUSE], //Holmvik_Brygge_Nyksund
    '476278': [CertificateType.SWAN_LABEL], //Scandic_Fornebu
    '606002': [CertificateType.ECO_LIGHTHOUSE], //Hurdalsjoen_Hotel
    '476284': [CertificateType.ECO_LIGHTHOUSE], //Solvgarden_Hotel
    '545793': [CertificateType.SWAN_LABEL], //Scandic_Ornen
    '2056247': [CertificateType.SWAN_LABEL], //Scandic_Stavanger_City
    '2065151': [CertificateType.ISO_14001], //Clarion_Hotel_The_Edge
    '2173985': [CertificateType.ISO_14001], //Comfort_Hotel_Xpress_Tromso
    '644295': [CertificateType.ECO_LIGHTHOUSE], //Ona_Havstuer_By_Classic_Norway_Hotels
    '2478902': [CertificateType.ISO_14001], //Quality_Hotel_Pond
    '3486537': [CertificateType.ECO_LIGHTHOUSE], //Sure_Hotel_by_Best_Western_Harstad_Narvik_Airport
    '2768773': [CertificateType.ECO_LIGHTHOUSE], //Kongsvinger_Castle_Hotel_Resort
    '2944693': [CertificateType.ECO_LIGHTHOUSE], //Svalbard_Hotell_The_Vault
    '3702354': [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL], //Hotel_Norge_by_Scandic
    '5607761': [CertificateType.ECOTOURISM_NORWAY], //Kirkvollen_pilegrimsgard
    '13548': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Maritim
    '2216555': [CertificateType.ECO_LIGHTHOUSE], //Bekkjarvik_Gjestgiveri
    '422306': [CertificateType.ECO_LIGHTHOUSE], //Pellestova_Hotell_Hafjell
    '74757': [CertificateType.ECO_LIGHTHOUSE], //First_Hotel_Victoria
    '241736': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Halden
    '210314': [CertificateType.ECO_LIGHTHOUSE], //Flamsbrygga_Hotel
    '154067': [CertificateType.ECO_LIGHTHOUSE], //Losby_Gods_Manor
    '316816': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Kristiansund
    '396505': [CertificateType.ECO_LIGHTHOUSE], //Lampeland_Hotel
    '317993': [CertificateType.ECO_LIGHTHOUSE], //Vertshuset_Roros
    '179682': [CertificateType.ECO_LIGHTHOUSE], //Fru_Haugans_Hotel
    '339937': [CertificateType.ECO_LIGHTHOUSE], //Molde_Fjordstuer_by_Classic_Norway
    '80963': [CertificateType.SWAN_LABEL], //Scandic_Parken
    '3452': [CertificateType.SWAN_LABEL], //Scandic_Alta
    '7746886': [CertificateType.ECO_LIGHTHOUSE], //Grand_Hotel_Terminus
    '26813': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Park_Bergen
    '19697': [CertificateType.SWAN_LABEL], //Scandic_Neptun
    '31257': [CertificateType.SWAN_LABEL], //Scandic_Bergen_City
    '1365': [CertificateType.SWAN_LABEL], //Scandic_Sunnfjord_Hotel_Spa
    '59222': [CertificateType.SWAN_LABEL], //Scandic_Hamar
    '31151': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Kristiansand
    '1035': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Backlund
    '130231': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Astoria
    '74722': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Bristol_Oslo
    '13357': [CertificateType.SWAN_LABEL], //Scandic_Holberg
    '74756': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Sverre
    '140086': [CertificateType.ECO_LIGHTHOUSE], //Kronen_Gaard_Hotel
    '12492': [CertificateType.ISO_14001], //Quality_Hotel_Sarpsborg
    '59254': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Jolster
    '15643': [CertificateType.ISO_14001], //Quality_Hotel_Sogndal
    '2227': [CertificateType.ISO_14001], //Quality_Hotel_Stavanger_Airport
    '90359': [CertificateType.ECO_LIGHTHOUSE], //Sola_Strand_Hotel
    '176289': [CertificateType.ECO_LIGHTHOUSE], //St_Svithun_Hotel
    '59261': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Polar
    '259': [CertificateType.ISO_14001], //Quality_Hotel_Grand_Steinkjer
    '59224': [CertificateType.ISO_14001], //Quality_Hotel_Entry
    '35873': [CertificateType.GREEN_KEY], //Radisson_Blu_Scandinavia_Hotel_Oslo
    '256562': [CertificateType.GREEN_KEY], //Lillehammer_Stasjonen_Hotel_Hostel
    '11969': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Bergen_Airport
    '8206': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Nordlys
    '74751': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Geiranger
    '59242': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Cecil
    '34782': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Opera
    '13376': [CertificateType.SWAN_LABEL], //Scandic_Oslo_City
    '79924': [CertificateType.SWAN_LABEL], //Scandic_Asker
    '119097': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Otta
    '259397': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Oslo_Panorama
    '23341': [CertificateType.ECO_LIGHTHOUSE], //Best_Western_Tingvold_Park_Hotel
    '700857': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Union_Oye
    '336868': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Tromso
    '374153': [CertificateType.ECO_LIGHTHOUSE], //Best_Western_Leto_Arena
    '643990': [CertificateType.SWAN_LABEL], //Scandic_Rock_City
    '413622': [CertificateType.SWAN_LABEL], //Scandic_Vulkan
    '408699': [CertificateType.ISO_14001], //Quality_Hotel_Waterfront_Alesund
    '375373': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Bjorneparken
    '376809': [CertificateType.SWAN_LABEL], //Scandic_Stavanger_Forus
    '388529': [CertificateType.ECO_LIGHTHOUSE], //Cochs_Pensjonat
    '600539': [CertificateType.ECO_LIGHTHOUSE], //Dalen_Hotel
    '606132': [CertificateType.ECO_LIGHTHOUSE], //Strand_Hotel_Fevik_By_Classic_Norway_Hotels
    '13110': [CertificateType.ECO_LIGHTHOUSE], //Hotel_Refsnes_Gods
    '640459': [CertificateType.ISO_14001], //Quality_Hotel_Expo
    '663065': [CertificateType.SWAN_LABEL], //Farsund_Resort
    '456466': [CertificateType.ECO_LIGHTHOUSE], //Fjaerland_Fjordstue_Hotel
    '92309': [CertificateType.ECO_LIGHTHOUSE], //Grand_Hotel_Egersund
    '516464': [CertificateType.SWAN_LABEL], //Scandic_Kristiansand_Bystranda
    '711505': [CertificateType.ECO_LIGHTHOUSE], //Hallingdal_Feriepark
    '729204': [CertificateType.SWAN_LABEL], //Scandic_Havet
    '2077217': [CertificateType.ECO_LIGHTHOUSE], //Runde_Miljosenter
    '2202139': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Fosnavag
    '41141': [CertificateType.ISO_14001], //Clarion_Collection_Hotel_With
    '7747984': [CertificateType.ECO_LIGHTHOUSE], //Zander_K_Hotel
    '879511': [CertificateType.SWAN_LABEL], //Scandic_Flesland_Airport
    '7747009': [CertificateType.ECO_LIGHTHOUSE], //Bergen_Bors_Hotel
    '2623348': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Stavanger
    '2571263': [CertificateType.ECO_LIGHTHOUSE], //Valldal_Fjordhotell_By_Classic_Norway_Hotels
    '3930192': [CertificateType.ECO_LIGHTHOUSE], //Mandal_Hotel
    '3935247': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Storo
    '74823': [CertificateType.ECO_LIGHTHOUSE], //Ustedalen_Hotel_Hol
    '897104': [CertificateType.GREEN_KEY], //Radisson_Blu_Caledonien_Hotel_Kristiansand
    '74742': [CertificateType.ISO_14001], //Quality_Hotel_Grand_Farris
    '11685': [CertificateType.SWAN_LABEL, CertificateType.ISO_14001], //Best_Western_Havly_Hotell
    '31137': [CertificateType.SWAN_LABEL], //Scandic_Sjolyst
    '12933': [CertificateType.ISO_14001], //Quality_Hotel_Leangkollen
    '59252': [CertificateType.ISO_14001], //Quality_Hotel_Residence
    '309704': [CertificateType.ECO_LIGHTHOUSE], //Thon_Hotel_Ullevaal_Stadion
    '383340': [CertificateType.ECO_LIGHTHOUSE], //Henningsvaer_Bryggehotel_by_Classic_Norway
    '180643': [CertificateType.ECO_LIGHTHOUSE], //Kjobmandsgaarden_Hotel
    '487300': [CertificateType.GREEN_KEY], //Honne_Hotell_Konferansesenter
    '4257972': [CertificateType.ISO_14001], //Quality_Hotel_Alesund
    '5405685': [CertificateType.ECO_LIGHTHOUSE], //Hafjell_Resort_Jaertunet
    '432063': [CertificateType.ECO_LIGHTHOUSE], //Knutholmen
    '1070673794': [CertificateType.ECO_LIGHTHOUSE], //Lindstroem_Hotel
    '100938': [CertificateType.ECO_LIGHTHOUSE], //Engo_Gard_Hotel_Restaurant
    '600598': [CertificateType.ECO_LIGHTHOUSE], //Holmen_Fjordhotell
};
